import { NEUTRAL_POSE, type Accessory, type Character, type CharacterPose, type CharacterProportions } from '../domain/schemas';

/**
 * Procedural, vector-style characters built from structured data.
 *
 * `characterParts()` turns a definition into drawing primitives in a unit box
 * ([0,1] × [0,1], y down). The PixiJS renderer scales that box to an instance's
 * width/height; the character library draws the same primitives as SVG thumbnails. The
 * layout normalises the vertical stack (head + torso + legs + shoes) to always fill the
 * box, and clamps horizontal extents, so any valid proportions stay inside the box.
 */

export type CharacterPresetId = 'modern' | 'fantasy' | 'futuristic';

/**
 * Body segments a primitive belongs to (Phase 3B posing). Legs hang from the hips; the
 * torso turns around the hip centre; the head (neck) and arms (shoulders) follow it.
 */
export type Segment = 'torso' | 'head' | 'armL' | 'armR' | 'legL' | 'legR';
export const SEGMENTS: readonly Segment[] = ['torso', 'head', 'armL', 'armR', 'legL', 'legR'];

type PrimitiveShape =
  | { kind: 'rect'; x: number; y: number; w: number; h: number; color: string; radius?: number; part: string }
  | { kind: 'ellipse'; cx: number; cy: number; rx: number; ry: number; color: string; part: string }
  | { kind: 'poly'; points: number[]; color: string; part: string };
export type Primitive = PrimitiveShape & { segment: Segment };

export const CHARACTER_PRESETS: Record<CharacterPresetId, { label: string; character: Omit<Character, 'id'> }> = {
  modern: {
    label: 'Modern Runner',
    character: {
      name: 'Modern Runner',
      proportions: { headSize: 0.2, torsoWidth: 0.5, torsoHeight: 0.28, legLength: 0.36, armLength: 1, limbThickness: 0.1 },
      skin: '#f1c27d',
      head: { shape: 'round' },
      eyes: { style: 'dot', color: '#1f2937' },
      hair: { style: 'short', color: '#1c1917' },
      clothing: { top: 'tshirt', topColor: '#f97316', accentColor: '#ffffff', bottom: 'shorts', bottomColor: '#1e3a8a', shoeColor: '#f8fafc' },
      accessories: [
        { kind: 'headphones', color: '#111827' },
        { kind: 'backpack', color: '#0f766e' },
      ],
    },
  },
  fantasy: {
    label: 'Fantasy Adventurer',
    character: {
      name: 'Fantasy Adventurer',
      proportions: { headSize: 0.22, torsoWidth: 0.56, torsoHeight: 0.32, legLength: 0.3, armLength: 1.05, limbThickness: 0.11 },
      skin: '#e0ac69',
      head: { shape: 'oval' },
      eyes: { style: 'round', color: '#14532d' },
      hair: { style: 'long', color: '#9a3412' },
      clothing: { top: 'robe', topColor: '#166534', accentColor: '#facc15', bottom: 'robe', bottomColor: '#14532d', shoeColor: '#78350f' },
      accessories: [
        { kind: 'cape', color: '#b91c1c' },
        { kind: 'belt', color: '#78350f' },
        { kind: 'sword', color: '#cbd5e1' },
      ],
    },
  },
  futuristic: {
    label: 'Sci-Fi Explorer',
    character: {
      name: 'Sci-Fi Explorer',
      proportions: { headSize: 0.19, torsoWidth: 0.6, torsoHeight: 0.3, legLength: 0.38, armLength: 0.95, limbThickness: 0.12 },
      skin: '#c68642',
      head: { shape: 'square' },
      eyes: { style: 'visor', color: '#22d3ee' },
      hair: { style: 'mohawk', color: '#a855f7' },
      clothing: { top: 'armor', topColor: '#94a3b8', accentColor: '#22d3ee', bottom: 'pants', bottomColor: '#1e293b', shoeColor: '#0f172a' },
      accessories: [
        { kind: 'antenna', color: '#f472b6' },
        { kind: 'backpack', color: '#334155' },
      ],
    },
  },
};

export const CHARACTER_PRESET_IDS = Object.keys(CHARACTER_PRESETS) as CharacterPresetId[];

/** Natural width:height of a character instance (instances may stretch it). */
export const CHARACTER_ASPECT = 0.5;

/** Shade a #rrggbb colour by `amount` (-1 darker … +1 lighter). */
export function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const ch = (shift: number) => {
    const c = (n >> shift) & 0xff;
    const v = amount < 0 ? c * (1 + amount) : c + (255 - c) * amount;
    return Math.round(Math.min(255, Math.max(0, v)));
  };
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, '0')).join('')}`;
}

interface Layout {
  k: number;
  cx: number;
  headTop: number;
  headH: number;
  headW: number;
  torsoTop: number;
  torsoH: number;
  torsoW: number;
  legTop: number;
  legH: number;
  shoeH: number;
  limb: number;
  armLen: number;
}

const TOP_MARGIN = 0.06;
const SHOE = 0.035;
/** Width of the layout space in height units (the box is CHARACTER_ASPECT wide). */
const W = CHARACTER_ASPECT;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function characterLayout(p: CharacterProportions): Layout {
  // Scale the vertical stack so it always fills the box below the top margin.
  const k = (1 - TOP_MARGIN - 0.005) / (p.headSize + p.torsoHeight + p.legLength + SHOE);
  const headH = p.headSize * k;
  const headW = Math.min(headH * 0.92, W * 0.8);
  const torsoH = p.torsoHeight * k;
  const legH = p.legLength * k;
  const limb = clamp(p.limbThickness * W, 0.02, W * 0.18);
  const torsoW = clamp(p.torsoWidth * W, limb * 2.2, W - 2 * limb - 0.02);
  return {
    k,
    cx: W / 2,
    headTop: TOP_MARGIN,
    headH,
    headW,
    torsoTop: TOP_MARGIN + headH,
    torsoH,
    torsoW,
    legTop: TOP_MARGIN + headH + torsoH,
    legH,
    shoeH: SHOE * k,
    limb,
    armLen: Math.min(torsoH * p.armLength, 1 - (TOP_MARGIN + headH) - 0.02),
  };
}

/** Drawing primitives for a character, in a unit box, back to front. */
export function characterParts(c: Character): Primitive[] {
  const L = characterLayout(c.proportions);
  const out: Primitive[] = [];
  // Every primitive is tagged with the body segment that is current when it is drawn.
  let segment: Segment = 'torso';
  // Work in height units (x ∈ [0, W]); convert to the unit box at the end.
  const rect = (part: string, x: number, y: number, w: number, h: number, color: string, radius = 0) =>
    out.push({ kind: 'rect', part, x, y, w, h, color, radius, segment });
  const ellipse = (part: string, cx: number, cy: number, rx: number, ry: number, color: string) =>
    out.push({ kind: 'ellipse', part, cx, cy, rx, ry, color, segment });
  const poly = (part: string, color: string, ...pts: number[]) => out.push({ kind: 'poly', part, points: pts, color, segment });
  const has = (kind: Accessory['kind']) => c.accessories.find((a) => a.kind === kind);
  const { cx } = L;
  const torsoX = cx - L.torsoW / 2;
  const headCY = L.headTop + L.headH / 2;
  const skin = c.skin;

  // Back layer: cape, backpack, long hair.
  const cape = has('cape');
  if (cape) {
    const w = Math.min(W - 0.01, L.torsoW * 1.25);
    poly('cape', shade(cape.color, -0.15), cx - L.torsoW / 2, L.torsoTop, cx + L.torsoW / 2, L.torsoTop, cx + w / 2, L.legTop + L.legH * 0.8, cx - w / 2, L.legTop + L.legH * 0.8);
  }
  const pack = has('backpack');
  if (pack) rect('backpack', cx - L.torsoW * 0.42, L.torsoTop + L.torsoH * 0.1, L.torsoW * 0.84, L.torsoH * 0.7, shade(pack.color, -0.1), 0.02);
  segment = 'head';
  if (c.hair.style === 'long') rect('hair-back', cx - L.headW * 0.58, headCY - L.headH * 0.2, L.headW * 1.16, L.headH * 1.05, shade(c.hair.color, -0.12), 0.04);

  // Legs and shoes.
  const legW = L.limb;
  const legGap = Math.min(L.torsoW * 0.18, 0.04);
  for (const side of [-1, 1]) {
    segment = side < 0 ? 'legL' : 'legR';
    const x = side < 0 ? cx - legGap / 2 - legW : cx + legGap / 2;
    const bottomColor = c.clothing.bottom === 'shorts' ? skin : c.clothing.bottomColor;
    rect('leg', x, L.legTop, legW, L.legH, bottomColor);
    if (c.clothing.bottom === 'shorts') rect('shorts', x - 0.004, L.legTop, legW + 0.008, L.legH * 0.4, c.clothing.bottomColor);
    rect('shoe', side < 0 ? x - legW * 0.35 : x, L.legTop + L.legH, legW * 1.35, L.shoeH, c.clothing.shoeColor, 0.01);
  }
  segment = 'torso';
  if (c.clothing.bottom === 'skirt') poly('skirt', c.clothing.bottomColor, torsoX, L.legTop - 0.01, torsoX + L.torsoW, L.legTop - 0.01, cx + L.torsoW * 0.65, L.legTop + L.legH * 0.45, cx - L.torsoW * 0.65, L.legTop + L.legH * 0.45);
  if (c.clothing.bottom === 'robe' || c.clothing.top === 'robe') {
    poly('robe', c.clothing.bottomColor, torsoX, L.legTop - 0.02, torsoX + L.torsoW, L.legTop - 0.02, cx + L.torsoW * 0.62, L.legTop + L.legH * 0.85, cx - L.torsoW * 0.62, L.legTop + L.legH * 0.85);
  }

  // Arms (behind the torso edge so shoulders look attached).
  const sleeve = c.clothing.top === 'tshirt' ? skin : c.clothing.topColor;
  for (const side of [-1, 1]) {
    segment = side < 0 ? 'armL' : 'armR';
    const x = side < 0 ? Math.max(0.003, torsoX - L.limb * 0.85) : Math.min(W - L.limb - 0.003, torsoX + L.torsoW - L.limb * 0.15);
    rect('arm', x, L.torsoTop + 0.01, L.limb, L.armLen, sleeve, L.limb / 2);
    if (c.clothing.top === 'tshirt') rect('sleeve', x - 0.002, L.torsoTop + 0.01, L.limb + 0.004, L.armLen * 0.3, c.clothing.topColor, 0.01);
    ellipse('hand', x + L.limb / 2, L.torsoTop + 0.01 + L.armLen, L.limb * 0.6, L.limb * 0.6, skin);
  }

  // Torso and clothing details.
  segment = 'torso';
  const topColor = c.clothing.topColor;
  rect('torso', torsoX, L.torsoTop, L.torsoW, L.torsoH, topColor, c.clothing.top === 'armor' ? 0.01 : 0.03);
  const accent = c.clothing.accentColor;
  switch (c.clothing.top) {
    case 'tshirt':
      rect('stripe', torsoX, L.torsoTop + L.torsoH * 0.45, L.torsoW, L.torsoH * 0.1, accent);
      break;
    case 'hoodie':
      ellipse('hood-collar', cx, L.torsoTop + 0.01, L.torsoW * 0.32, L.torsoH * 0.1, shade(topColor, -0.2));
      rect('pocket', cx - L.torsoW * 0.25, L.torsoTop + L.torsoH * 0.6, L.torsoW * 0.5, L.torsoH * 0.22, shade(topColor, -0.12), 0.02);
      break;
    case 'robe':
      poly('robe-trim', accent, cx - 0.01, L.torsoTop, cx + 0.01, L.torsoTop, cx + 0.01, L.legTop, cx - 0.01, L.legTop);
      break;
    case 'armor':
      rect('chest-plate', torsoX + L.torsoW * 0.15, L.torsoTop + L.torsoH * 0.12, L.torsoW * 0.7, L.torsoH * 0.38, shade(topColor, 0.25), 0.015);
      ellipse('core', cx, L.torsoTop + L.torsoH * 0.31, L.torsoW * 0.1, L.torsoW * 0.1, accent);
      rect('armor-band', torsoX, L.torsoTop + L.torsoH * 0.78, L.torsoW, L.torsoH * 0.08, accent);
      break;
    case 'suit':
      poly('lapel', accent, cx - L.torsoW * 0.2, L.torsoTop, cx + L.torsoW * 0.2, L.torsoTop, cx, L.torsoTop + L.torsoH * 0.45);
      rect('tie', cx - 0.008, L.torsoTop + L.torsoH * 0.05, 0.016, L.torsoH * 0.5, shade(accent, -0.4));
      break;
  }
  const belt = has('belt');
  if (belt) rect('belt', torsoX, L.legTop - L.torsoH * 0.12, L.torsoW, L.torsoH * 0.09, belt.color);
  const scarf = has('scarf');
  if (scarf) rect('scarf', torsoX + L.torsoW * 0.1, L.torsoTop - 0.005, L.torsoW * 0.8, L.torsoH * 0.12, scarf.color, 0.02);
  const sword = has('sword');
  if (sword) {
    const x = Math.min(W - 0.02, torsoX + L.torsoW + L.limb * 0.6);
    rect('sword-blade', x, L.legTop - L.torsoH * 0.6, 0.014, L.torsoH * 0.9, sword.color);
    rect('sword-hilt', x - 0.012, L.legTop + L.torsoH * 0.28, 0.038, 0.012, shade(sword.color, -0.5));
  }

  // Head.
  segment = 'head';
  const headX = cx - L.headW / 2;
  if (has('hood')) ellipse('hood', cx, headCY, L.headW * 0.68, L.headH * 0.62, has('hood')!.color);
  if (c.head.shape === 'round') ellipse('head', cx, headCY, L.headW / 2, L.headH / 2, skin);
  else if (c.head.shape === 'oval') ellipse('head', cx, headCY, L.headW * 0.44, L.headH / 2, skin);
  else rect('head', headX, L.headTop, L.headW, L.headH, skin, L.headW * 0.18);

  // Eyes.
  const eyeY = headCY + L.headH * 0.02;
  const eyeDX = L.headW * 0.2;
  switch (c.eyes.style) {
    case 'dot':
      for (const s of [-1, 1]) ellipse('eye', cx + s * eyeDX, eyeY, L.headW * 0.05, L.headW * 0.05, c.eyes.color);
      break;
    case 'round':
      for (const s of [-1, 1]) {
        ellipse('eye-white', cx + s * eyeDX, eyeY, L.headW * 0.1, L.headW * 0.1, '#ffffff');
        ellipse('eye', cx + s * eyeDX, eyeY, L.headW * 0.055, L.headW * 0.055, c.eyes.color);
      }
      break;
    case 'sleepy':
      for (const s of [-1, 1]) rect('eye', cx + s * eyeDX - L.headW * 0.08, eyeY, L.headW * 0.16, L.headW * 0.035, c.eyes.color);
      break;
    case 'visor':
      rect('visor', headX + L.headW * 0.08, eyeY - L.headH * 0.1, L.headW * 0.84, L.headH * 0.2, c.eyes.color, 0.01);
      break;
  }
  const glasses = has('glasses');
  if (glasses) for (const s of [-1, 1]) ellipse('glasses', cx + s * eyeDX, eyeY, L.headW * 0.13, L.headW * 0.11, glasses.color);

  // Hair (front).
  const hairTop = L.headTop - L.headH * 0.04;
  switch (c.hair.style) {
    case 'short':
      rect('hair', headX - 0.004, hairTop, L.headW + 0.008, L.headH * 0.3, c.hair.color, 0.03);
      break;
    case 'long':
      rect('hair', headX - 0.006, hairTop, L.headW + 0.012, L.headH * 0.32, c.hair.color, 0.04);
      break;
    case 'spiky':
      poly('hair', c.hair.color, headX, L.headTop + L.headH * 0.25, headX + L.headW * 0.2, hairTop - 0.02, headX + L.headW * 0.4, L.headTop + 0.01, headX + L.headW * 0.55, hairTop - 0.025, headX + L.headW * 0.75, L.headTop + 0.01, headX + L.headW * 0.9, hairTop - 0.015, headX + L.headW, L.headTop + L.headH * 0.25);
      break;
    case 'bun':
      rect('hair', headX - 0.004, hairTop, L.headW + 0.008, L.headH * 0.28, c.hair.color, 0.03);
      {
        // Keep the bun inside the box: its radius never exceeds its distance from the top.
        const cy = Math.max(0.012, hairTop - 0.004);
        ellipse('bun', cx, cy, L.headW * 0.2, Math.min(L.headW * 0.18, cy), c.hair.color);
      }
      break;
    case 'mohawk':
      rect('hair', cx - L.headW * 0.1, Math.max(0.006, hairTop - 0.025), L.headW * 0.2, L.headH * 0.32, c.hair.color, 0.01);
      break;
    case 'none':
      break;
  }

  // Head accessories.
  const hat = has('hat');
  if (hat) {
    rect('hat-brim', headX - L.headW * 0.12, L.headTop + L.headH * 0.05, L.headW * 1.24, L.headH * 0.08, shade(hat.color, -0.2));
    rect('hat', headX + L.headW * 0.1, Math.max(0.004, L.headTop - L.headH * 0.2), L.headW * 0.8, L.headH * 0.28, hat.color, 0.01);
  }
  const phones = has('headphones');
  if (phones) {
    rect('headphone-band', headX - 0.004, Math.max(0.004, hairTop - 0.008), L.headW + 0.008, 0.014, phones.color, 0.007);
    for (const s of [-1, 1]) ellipse('headphone', cx + s * (L.headW / 2), headCY, L.headW * 0.11, L.headH * 0.17, phones.color);
  }
  const antenna = has('antenna');
  if (antenna) {
    rect('antenna', cx + L.headW * 0.25, 0.012, 0.008, L.headTop - 0.012, shade(antenna.color, -0.3));
    ellipse('antenna-tip', cx + L.headW * 0.25 + 0.004, 0.012, 0.01, 0.01, antenna.color);
  }

  return out.map((p) => toUnitBox(p));
}

/** Joint positions in the unit box (pivots of the body segments). */
export type Rig = Record<Segment, { x: number; y: number }>;

export function characterRig(c: Character): Rig {
  const L = characterLayout(c.proportions);
  const { cx } = L;
  const legW = L.limb;
  const legGap = Math.min(L.torsoW * 0.18, 0.04);
  const torsoX = cx - L.torsoW / 2;
  const armX = (side: number) => (side < 0 ? Math.max(0.003, torsoX - L.limb * 0.85) : Math.min(W - L.limb - 0.003, torsoX + L.torsoW - L.limb * 0.15));
  const shoulderY = L.torsoTop + 0.01 + L.limb / 2;
  const u = (x: number, y: number) => ({ x: x / W, y });
  return {
    torso: u(cx, L.legTop),
    head: u(cx, L.torsoTop),
    armL: u(armX(-1) + L.limb / 2, shoulderY),
    armR: u(armX(1) + L.limb / 2, shoulderY),
    legL: u(cx - legGap / 2 - legW / 2, L.legTop),
    legR: u(cx + legGap / 2 + legW / 2, L.legTop),
  };
}

/**
 * Handle points for the pose editor, in the unit box: each segment's joint and the far end
 * it points to at rest (feet, hands, top of the head, the neck for the torso).
 */
export function characterHandles(c: Character): Record<Segment, { joint: { x: number; y: number }; end: { x: number; y: number } }> {
  const L = characterLayout(c.proportions);
  const rig = characterRig(c);
  const end = (x: number, y: number) => ({ x, y });
  return {
    torso: { joint: rig.torso, end: end(rig.torso.x, L.torsoTop) },
    head: { joint: rig.head, end: end(rig.head.x, L.headTop) },
    armL: { joint: rig.armL, end: end(rig.armL.x, L.torsoTop + 0.01 + L.armLen) },
    armR: { joint: rig.armR, end: end(rig.armR.x, L.torsoTop + 0.01 + L.armLen) },
    legL: { joint: rig.legL, end: end(rig.legL.x, L.legTop + L.legH) },
    legR: { joint: rig.legR, end: end(rig.legR.x, L.legTop + L.legH) },
  };
}

/** Where a segment is drawn: rotate by `rotation` (radians) about `pivot`, then place the pivot at `position`. */
export interface SegmentTransform {
  pivotX: number;
  pivotY: number;
  x: number;
  y: number;
  rotation: number;
}

/**
 * Pose → per-segment transforms in pixels for an instance of size `width` × `height`.
 * Angles are degrees, clockwise on screen; offsets are fractions of the height. Legs
 * pivot at the hips; the torso at the hip centre (plus its offset); head and arms are
 * attached to the torso, so they follow its rotation and offset. Each transform is
 * rigid in pixel space, so limbs never shear on stretched instances.
 */
export function segmentTransforms(rig: Rig, pose: CharacterPose, width: number, height: number): Record<Segment, SegmentTransform> {
  const rad = (deg: number) => (deg * Math.PI) / 180;
  const px = (p: { x: number; y: number }) => ({ x: p.x * width, y: p.y * height });
  const hip = px(rig.torso);
  const torsoAt = { x: hip.x + pose.torsoX * height, y: hip.y + pose.torsoY * height };
  const t = rad(pose.torso);
  const cos = Math.cos(t);
  const sin = Math.sin(t);
  // Torso transform applied to a point in the unposed drawing.
  const viaTorso = (p: { x: number; y: number }) => ({ x: torsoAt.x + (p.x - hip.x) * cos - (p.y - hip.y) * sin, y: torsoAt.y + (p.x - hip.x) * sin + (p.y - hip.y) * cos });
  const seg = (pivot: { x: number; y: number }, at: { x: number; y: number }, deg: number): SegmentTransform => ({ pivotX: pivot.x, pivotY: pivot.y, x: at.x, y: at.y, rotation: rad(deg) });
  const neck = px(rig.head);
  const neckAt = viaTorso(neck);
  const shoulderL = px(rig.armL);
  const shoulderR = px(rig.armR);
  return {
    torso: seg(hip, torsoAt, pose.torso),
    head: seg(neck, { x: neckAt.x + pose.headX * height, y: neckAt.y + pose.headY * height }, pose.torso + pose.head),
    armL: seg(shoulderL, viaTorso(shoulderL), pose.torso + pose.armL),
    armR: seg(shoulderR, viaTorso(shoulderR), pose.torso + pose.armR),
    legL: seg(px(rig.legL), px(rig.legL), pose.legL),
    legR: seg(px(rig.legR), px(rig.legR), pose.legR),
  };
}

/** Maps a point of the unposed drawing (pixels) through a segment transform. */
export function applySegment(t: SegmentTransform, p: { x: number; y: number }): { x: number; y: number } {
  const cos = Math.cos(t.rotation);
  const sin = Math.sin(t.rotation);
  const dx = p.x - t.pivotX;
  const dy = p.y - t.pivotY;
  return { x: t.x + dx * cos - dy * sin, y: t.y + dx * sin + dy * cos };
}

/**
 * Consecutive primitives of the same segment, in draw order. The renderer draws one
 * Graphics per run once per definition and poses runs by transform only.
 */
export function segmentRuns(parts: readonly Primitive[]): { segment: Segment; parts: Primitive[] }[] {
  const runs: { segment: Segment; parts: Primitive[] }[] = [];
  for (const p of parts) {
    const last = runs[runs.length - 1];
    if (last && last.segment === p.segment) last.parts.push(p);
    else runs.push({ segment: p.segment, parts: [p] });
  }
  return runs;
}

export const isNeutralPose = (pose: CharacterPose | undefined): boolean => !pose || Object.values(pose).every((v) => v === 0);

function toUnitBox(p: Primitive): Primitive {
  const sx = 1 / W;
  switch (p.kind) {
    case 'rect':
      return { ...p, x: p.x * sx, w: p.w * sx, radius: (p.radius ?? 0) * sx };
    case 'ellipse':
      return { ...p, cx: p.cx * sx, rx: p.rx * sx };
    case 'poly':
      return { ...p, points: p.points.map((v, i) => (i % 2 === 0 ? v * sx : v)) };
  }
}

/** Axis-aligned bounds of a primitive in the unit box. */
export function primitiveBounds(p: Primitive): { x0: number; y0: number; x1: number; y1: number } {
  switch (p.kind) {
    case 'rect':
      return { x0: p.x, y0: p.y, x1: p.x + p.w, y1: p.y + p.h };
    case 'ellipse':
      return { x0: p.cx - p.rx, y0: p.cy - p.ry, x1: p.cx + p.rx, y1: p.cy + p.ry };
    case 'poly': {
      const xs = p.points.filter((_, i) => i % 2 === 0);
      const ys = p.points.filter((_, i) => i % 2 === 1);
      return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
    }
  }
}

/** SVG markup for a character in a viewBox of 100 × 200 (thumbnails; no Pixi needed), optionally posed. */
export function characterSvg(c: Character, flipX = false, pose: CharacterPose = NEUTRAL_POSE): string {
  const X = 100;
  const Y = 200;
  const parts = characterParts(c);
  const shape = (p: Primitive) => {
    switch (p.kind) {
      case 'rect':
        return `<rect x="${(p.x * X).toFixed(2)}" y="${(p.y * Y).toFixed(2)}" width="${(p.w * X).toFixed(2)}" height="${(p.h * Y).toFixed(2)}" rx="${((p.radius ?? 0) * X).toFixed(2)}" fill="${p.color}"/>`;
      case 'ellipse':
        return `<ellipse cx="${(p.cx * X).toFixed(2)}" cy="${(p.cy * Y).toFixed(2)}" rx="${(p.rx * X).toFixed(2)}" ry="${(p.ry * Y).toFixed(2)}" fill="${p.color}"/>`;
      case 'poly':
        return `<polygon points="${p.points.map((v, i) => (v * (i % 2 === 0 ? X : Y)).toFixed(2)).join(' ')}" fill="${p.color}"/>`;
    }
  };
  let body: string;
  if (isNeutralPose(pose)) body = parts.map(shape).join('');
  else {
    const transforms = segmentTransforms(characterRig(c), pose, X, Y);
    body = segmentRuns(parts)
      .map((run) => {
        const t = transforms[run.segment];
        const deg = (t.rotation * 180) / Math.PI;
        return `<g data-segment="${run.segment}" transform="translate(${t.x.toFixed(2)} ${t.y.toFixed(2)}) rotate(${deg.toFixed(2)}) translate(${(-t.pivotX).toFixed(2)} ${(-t.pivotY).toFixed(2)})">${run.parts.map(shape).join('')}</g>`;
      })
      .join('');
  }
  const flip = flipX ? ` transform="translate(${X} 0) scale(-1 1)"` : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${X} ${Y}"><g${flip}>${body}</g></svg>`;
}
